import type { Components, Options } from "react-markdown";
import Markdown from "react-markdown";
import { Link } from "@tanstack/react-router";
import rehypeRaw from "rehype-raw";

type MarkdownRendererProps = {
  resetConfig?: boolean;
} & Options;

export const defaultConfig: Components = {
  a: ({ children, href, ref, ...props }) => {
    const invalidHref = !href || typeof href !== "string" || href.trim() === "";
    const isExternal = href?.startsWith("http");

    if (invalidHref) {
      return <span>{children}</span>;
    }

    if (isExternal) {
      return (
        <a
          className="font-semibold underline"
          href={href}
          ref={ref}
          rel="noopener noreferrer"
          target="_blank"
          {...props}
        >
          {children}
        </a>
      );
    }

    return (
      <Link className="font-semibold underline" to={href} {...props}>
        {children}
      </Link>
    );
  },
  p: (props) => {
    return <p {...props} className="text-content-secondary sm:text-xl" />;
  },
};

const allowedElements = ["br", "p", "a", "span", "div", "strong", "em", "ul", "ol", "li"];

export const MarkdownRenderer = ({
  children,
  components,
  rehypePlugins,
  resetConfig = false,
}: MarkdownRendererProps) => {
  const mergedConfig = { ...defaultConfig, ...components };
  const mergedPlugins = [rehypeRaw, ...(rehypePlugins ?? [])];

  return (
    <Markdown
      allowedElements={allowedElements}
      components={resetConfig ? undefined : mergedConfig}
      rehypePlugins={mergedPlugins}
    >
      {children}
    </Markdown>
  );
};
